// A number panel you can read inside the headset.
//
// VR spawn/collision problems are invisible from a desktop: the symptom is
// "I'm in the wrong place" and every explanation is a guess. This pins the
// facts to the rig — where it is, what the world says the floor is, how many
// collidable meshes exist, and what surface is under you — so a report can be
// four numbers instead of a description.
//
// Opt-in: add ?debug=1 to the world URL. Never shown otherwise.

import React, { useRef, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { Box3, Mesh } from 'three';
import type { Group } from 'three';
import { useThrottledFrame } from '../../hooks/useThrottledFrame';
import { useWorldStore } from '../../stores/worldStore';

const _box = new Box3();

export const vrDebugEnabled = (): boolean => {
  try { return new URLSearchParams(window.location.search).has('debug'); } catch { return false; }
};

const VRDebugReadout: React.FC<{ originRef: React.RefObject<Group | null> }> = ({ originRef }) => {
  const { scene, camera } = useThree();
  const [text, setText] = useState('…');
  const last = useRef(0);
  const spawn = useWorldStore((s) => s.spawnPosition);

  useThrottledFrame(() => {
    const now = performance.now();
    if (now - last.current < 400) return;
    last.current = now;
    const o = originRef.current;
    if (!o) return;

    let count = 0;
    let support = -Infinity;
    let lowest = Infinity;
    let highest = -Infinity;
    scene.traverse((obj) => {
      if (obj.userData?.collidable !== true || !(obj as Mesh).isMesh) return;
      count++;
      _box.setFromObject(obj);
      if (_box.isEmpty()) return;
      if (_box.max.y > highest) highest = _box.max.y;
      if (_box.min.y < lowest) lowest = _box.min.y;
      if (o.position.x < _box.min.x - 0.05 || o.position.x > _box.max.x + 0.05) return;
      if (o.position.z < _box.min.z - 0.05 || o.position.z > _box.max.z + 0.05) return;
      if (_box.max.y > support) support = _box.max.y;
    });

    const f = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : '—');
    setText(
      `rig  ${f(o.position.x)}, ${f(o.position.y)}, ${f(o.position.z)}\n` +
      `head y  ${f(camera.position.y)}\n` +
      `spawn  ${f(spawn[0])}, ${f(spawn[1])}, ${f(spawn[2])}\n` +
      `colliders  ${count}\n` +
      `under me  ${f(support)}\n` +
      `collider span  ${f(lowest)} … ${f(highest)}`
    );
  }, 6, true);

  return (
    <group position={[0, 1.3, -1.1]}>
      <mesh>
        <planeGeometry args={[0.85, 0.5]} />
        <meshBasicMaterial color="#04121c" transparent opacity={0.82} />
      </mesh>
      <Text
        position={[0, 0, 0.01]}
        fontSize={0.045}
        color="#7df3e7"
        anchorX="center"
        anchorY="middle"
        textAlign="left"
        maxWidth={0.8}
      >
        {text}
      </Text>
    </group>
  );
};

export default VRDebugReadout;
